import styled from "styled-components";
import {
  Heading,
  Text,
  VStack,
  Box,
  Image,
  Button as ChakraButton,
  SimpleGrid,
  HStack,
} from "@chakra-ui/react";
import { FaRocket, FaChartLine, FaUsers, FaHeart } from "react-icons/fa";
import type { IconType } from "react-icons";
import skipgiveIcon from "../../assets/skipgive-icon.png";
import { useNavigate } from "react-router-dom";
import { Show } from "../Show";
import { useAuth } from "../../hooks/useAuth";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 80vh;
  padding: 2rem 1rem;
`;

const ActionCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(51, 156, 255, 0.1);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;

  &:hover {
    border-color: #339cff;
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(51, 156, 255, 0.2);
  }
`;

const QuoteCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.3);
  max-width: 600px;
  margin: 2rem auto;
  position: relative;
  overflow: hidden;

  &::before {
    content: '"';
    position: absolute;
    top: -20px;
    left: 20px;
    font-size: 120px;
    color: rgba(255, 255, 255, 0.1);
    font-family: Georgia, serif;
  }
`;

const IconWrapper = styled.span<{ $size?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => props.$size || 32}px;
`;

const Icon = ({
  icon: IconComponent,
  size,
}: {
  icon: IconType;
  size?: number;
}) => {
  return <IconWrapper $size={size}>{IconComponent({})}</IconWrapper>;
};

export const Welcome = () => {
  const navigate = useNavigate();
  const { onboardingResults } = useAuth();
  const isIndividual = onboardingResults.isIndividual;

  const welcome = (
    <VStack spacing={6}>
      <Box textAlign="center">
        <Heading size="2xl" color="#1A202C" fontWeight="800" mb={4}>
          {isIndividual ? "Raise for a Cause You Believe In" : "The Ultimate Fundraising Tool"}
        </Heading>
        <Text
          fontSize="lg"
          color="gray.600"
          maxW="600px"
          textAlign="center"
          mb={6}
          fontWeight="500"
        >
          {isIndividual ? (
            <>
              As an individual fundraiser, you can create campaigns on behalf of
              registered{" "}
              <Text as="span" color="#339CFF" fontWeight="700">
                501(c)(3) nonprofit organizations
              </Text>
              . Connect your bank account to get started, then we'll help you
              launch your first campaign for the cause you care about.
            </>
          ) : (
            <>
              Through the power of{" "}
              <Text as="span" color="#339CFF" fontWeight="700">
                trust
              </Text>{" "}
              and{" "}
              <Text as="span" color="#339CFF" fontWeight="700">
                mobile giving
              </Text>
              , SkipGive enables you to effortlessly increase donations from your
              existing network. Let's level up your next raise!
            </>
          )}
        </Text>
      </Box>
      <ChakraButton
        size="lg"
        bg="#339CFF"
        color="white"
        px={8}
        py={6}
        borderRadius="12px"
        fontSize="lg"
        fontWeight="700"
        boxShadow="0 10px 30px rgba(51, 156, 255, 0.4)"
        _hover={{
          bg: "#1a365d",
          transform: "translateY(-2px)",
          boxShadow: "0 15px 40px rgba(51, 156, 255, 0.5)",
        }}
        transition="all 0.3s"
        onClick={() => navigate("/dashboard/start")}
      >
        Start Fundraising
      </ChakraButton>
    </VStack>
  );

  const welcomeBack = (
    <VStack spacing={8} width="100%">
      <Box textAlign="center">
        <Heading size="2xl" color="#1A202C" fontWeight="800" mb={2}>
          Welcome Back! 👋
        </Heading>
        <Text fontSize="xl" color="gray.600" fontWeight="500">
          Ready to make a difference?
        </Text>
      </Box>

      <QuoteCard>
        <Text
          fontSize="2xl"
          color="white"
          fontWeight="700"
          fontStyle="italic"
          lineHeight="1.6"
          position="relative"
          zIndex={1}
        >
          Let's do something good today.
        </Text>
        <Text
          fontSize="md"
          color="rgba(255, 255, 255, 0.9)"
          mt={3}
          position="relative"
          zIndex={1}
        >
          Every action counts. Every donor matters.
        </Text>
      </QuoteCard>

      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 4 }}
        spacing={6}
        width="100%"
        maxW="1200px"
      >
        <ActionCard onClick={() => navigate("/dashboard/analytics")}>
          <Box
            p={4}
            borderRadius="full"
            bg="rgba(51, 156, 255, 0.1)"
            color="#339CFF"
          >
            <Icon icon={FaRocket} size={32} />
          </Box>
          <VStack spacing={2}>
            <Heading size="md" color="#1A202C">
              New Campaign
            </Heading>
            <Text fontSize="sm" color="gray.600">
              Launch a fundraiser
            </Text>
          </VStack>
        </ActionCard>

        <ActionCard onClick={() => navigate("/dashboard/analytics")}>
          <Box
            p={4}
            borderRadius="full"
            bg="rgba(102, 126, 234, 0.1)"
            color="#667eea"
          >
            <Icon icon={FaChartLine} size={32} />
          </Box>
          <VStack spacing={2}>
            <Heading size="md" color="#1A202C">
              View Analytics
            </Heading>
            <Text fontSize="sm" color="gray.600">
              Track your progress
            </Text>
          </VStack>
        </ActionCard>

        <ActionCard onClick={() => navigate("/dashboard/contacts")}>
          <Box
            p={4}
            borderRadius="full"
            bg="rgba(255, 107, 53, 0.1)"
            color="#FF6B35"
          >
            <Icon icon={FaUsers} size={32} />
          </Box>
          <VStack spacing={2}>
            <Heading size="md" color="#1A202C">
              Manage Contacts
            </Heading>
            <Text fontSize="sm" color="gray.600">
              Connect with donors
            </Text>
          </VStack>
        </ActionCard>

        <ActionCard onClick={() => navigate("/dashboard/payments")}>
          <Box
            p={4}
            borderRadius="full"
            bg="rgba(0, 200, 81, 0.1)"
            color="#00C851"
          >
            <Icon icon={FaHeart} size={32} />
          </Box>
          <VStack spacing={2}>
            <Heading size="md" color="#1A202C">
              Donations
            </Heading>
            <Text fontSize="sm" color="gray.600">
              See your impact
            </Text>
          </VStack>
        </ActionCard>
      </SimpleGrid>

      <Box
        bg="rgba(51, 156, 255, 0.05)"
        p={6}
        borderRadius="16px"
        borderLeft="4px solid #339CFF"
        maxW="800px"
        width="100%"
      >
        <HStack spacing={3} align="start">
          <Text fontSize="2xl">💡</Text>
          <VStack align="start" spacing={1}>
            <Text fontSize="md" fontWeight="700" color="#1A202C">
              Pro Tip
            </Text>
            <Text fontSize="sm" color="gray.700">
              Schedule your campaigns ahead of time and use personalized
              messages to increase engagement. Donors are 3x more likely to give
              when they receive a personal touch!
            </Text>
          </VStack>
        </HStack>
      </Box>
    </VStack>
  );

  return (
    <Container>
      <Show
        when={onboardingResults.charges && onboardingResults.details}
        whenFalseContent={
          <>
            <Image
              src={skipgiveIcon}
              alt="SkipGive"
              height="120px"
              mb={8}
              filter="drop-shadow(0 20px 40px rgba(51, 156, 255, 0.3))"
            />
            {welcome}
          </>
        }
      >
        {welcomeBack}
      </Show>
    </Container>
  );
};
